import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SWIPE_ROUTE_ORDER } from "../config/swipeRouteOrder";

const SWIPE_THRESHOLD_PX = 180;
const GESTURE_IDLE_MS = 180;
const NAVIGATION_COOLDOWN_MS = 700;
const HORIZONTAL_INTENT_RATIO = 1.2;

let globalCooldownUntil = 0;

function isMacDesktopEnvironment() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const platform =
    navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || "";
  const isMacLike = /Mac|Macintosh|MacIntel|MacPPC|Mac68K|Mac OS X/i.test(String(platform));
  const hasFinePointer = window.matchMedia?.("(pointer: fine)")?.matches ?? true;
  const hasHover = window.matchMedia?.("(hover: hover)")?.matches ?? true;
  return isMacLike && hasFinePointer && hasHover;
}

function isEditableTarget(node) {
  if (!(node instanceof Element)) return false;
  return Boolean(
    node.closest(
      'input, textarea, select, option, button, [contenteditable=""], [contenteditable="true"]'
    )
  );
}

function isInsideOverlay(node) {
  if (!(node instanceof Element)) return false;
  return Boolean(
    node.closest(
      [
        '[role="dialog"]',
        '[aria-modal="true"]',
        '[role="menu"]',
        '[role="listbox"]',
        ".modal",
        ".modal-overlay",
        ".dropdown-menu",
        ".menu",
        ".popover",
        ".ReactModalPortal",
        ".report-viewer-overlay",
        ".cinema-report-overlay",
        "[data-radix-popper-content-wrapper]",
        "[data-floating-ui-portal]",
      ].join(", ")
    )
  );
}

function hasHorizontalScrollParent(node) {
  let current = node instanceof Element ? node : null;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const overflowX = style.overflowX;
    const isScrollable = overflowX === "auto" || overflowX === "scroll";
    const canScrollHorizontally = current.scrollWidth > current.clientWidth + 4;
    if (isScrollable && canScrollHorizontally) return true;
    current = current.parentElement;
  }
  return false;
}

// `routes` is the governed previous/next sequence for the current session —
// pages the current context is not eligible for must already be absent from it.
// The default keeps ungoverned callers on the full canonical order.
export default function useGlobalSwipeNavigation({
  disabled = false,
  routes = SWIPE_ROUTE_ORDER,
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const deltaXRef = useRef(0);
  const gestureActiveRef = useRef(false);
  const cooldownUntilRef = useRef(0);
  const lastWheelAtRef = useRef(0);

  useEffect(() => {
    if (disabled) return undefined;
    if (!isMacDesktopEnvironment()) return undefined;
    if (!Array.isArray(routes) || !routes.includes(location.pathname)) {
      return undefined;
    }

    const resetGesture = () => {
      deltaXRef.current = 0;
      gestureActiveRef.current = false;
    };

    const handleWheel = (event) => {
      const now = Date.now();
      if (now < cooldownUntilRef.current || now < globalCooldownUntil) return;

      const target = event.target;
      if (isEditableTarget(target)) return;
      if (isInsideOverlay(target)) return;
      if (hasHorizontalScrollParent(target)) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const absDeltaX = Math.abs(event.deltaX);
      const absDeltaY = Math.abs(event.deltaY);
      if (absDeltaX < 6) return;
      if (absDeltaX <= absDeltaY * HORIZONTAL_INTENT_RATIO) return;

      if (now - lastWheelAtRef.current > GESTURE_IDLE_MS) {
        resetGesture();
      }
      lastWheelAtRef.current = now;

      deltaXRef.current += event.deltaX;
      gestureActiveRef.current = true;

      if (Math.abs(deltaXRef.current) < SWIPE_THRESHOLD_PX) return;

      const currentIndex = routes.indexOf(location.pathname);
      if (currentIndex === -1) {
        resetGesture();
        return;
      }

      const direction = deltaXRef.current > 0 ? 1 : -1;
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= routes.length) {
        cooldownUntilRef.current = now + NAVIGATION_COOLDOWN_MS;
        globalCooldownUntil = cooldownUntilRef.current;
        resetGesture();
        return;
      }

      const nextPath = routes[nextIndex];
      if (!nextPath || nextPath === location.pathname) {
        resetGesture();
        return;
      }

      cooldownUntilRef.current = now + NAVIGATION_COOLDOWN_MS;
      globalCooldownUntil = cooldownUntilRef.current;
      resetGesture();
      navigate(nextPath);
    };

    const handlePointerRelease = () => {
      if (!gestureActiveRef.current) return;
      if (Date.now() - lastWheelAtRef.current >= GESTURE_IDLE_MS) {
        resetGesture();
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("mouseup", handlePointerRelease);
    window.addEventListener("blur", resetGesture);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("mouseup", handlePointerRelease);
      window.removeEventListener("blur", resetGesture);
      resetGesture();
    };
  }, [disabled, location.pathname, navigate, routes]);
}
